export interface BaselineTeamProjection {
  abbr: string;
  projectedWins: number;
  fpi: number;
  fpiRank: number;
  sosRank: number;
  byeWeek: number;
  tier: 1 | 2 | 3 | 4 | 5; // 1 = Super Bowl Contenders, 2 = Playoff Locks, 3 = Bubble, 4 = Rebuilding, 5 = Cellar
  keyStars: string[];
  notes: string;
}

export const BASELINE_PROJECTIONS: Record<string, BaselineTeamProjection> = {
  KC: {
    abbr: 'KC',
    projectedWins: 11.8,
    fpi: 7.8,
    fpiRank: 1,
    sosRank: 14,
    byeWeek: 6,
    tier: 1,
    keyStars: ['Patrick Mahomes (QB)', 'Travis Kelce (TE)', 'Chris Jones (DT)'],
    notes: 'Perennial AFC favorites with unmatched playoff pedigree and offensive ceiling.',
  },
  SF: {
    abbr: 'SF',
    projectedWins: 11.4,
    fpi: 7.2,
    fpiRank: 2,
    sosRank: 12,
    byeWeek: 9,
    tier: 1,
    keyStars: ['Christian McCaffrey (RB)', 'Brock Purdy (QB)', 'Nick Bosa (DE)'],
    notes: 'Elite two-way roster efficiency with explosive offensive skill weapons.',
  },
  BAL: {
    abbr: 'BAL',
    projectedWins: 11.2,
    fpi: 7.0,
    fpiRank: 3,
    sosRank: 8,
    byeWeek: 14,
    tier: 1,
    keyStars: ['Lamar Jackson (QB)', 'Derrick Henry (RB)', 'Kyle Hamilton (S)'],
    notes: 'Powerhouse rushing attack and dominant turnover-forcing defense.',
  },
  DET: {
    abbr: 'DET',
    projectedWins: 10.9,
    fpi: 6.5,
    fpiRank: 4,
    sosRank: 18,
    byeWeek: 5,
    tier: 1,
    keyStars: ['Jared Goff (QB)', 'Amon-Ra St. Brown (WR)', 'Aidan Hutchinson (DE)'],
    notes: 'Top-tier offensive line and elite home dome scoring potency.',
  },
  BUF: {
    abbr: 'BUF',
    projectedWins: 10.6,
    fpi: 6.1,
    fpiRank: 5,
    sosRank: 10,
    byeWeek: 12,
    tier: 1,
    keyStars: ['Josh Allen (QB)', 'James Cook (RB)', 'Ed Oliver (DT)'],
    notes: 'Josh Allen provides singular dual-threat upside every week.',
  },
  PHI: {
    abbr: 'PHI',
    projectedWins: 10.5,
    fpi: 5.8,
    fpiRank: 6,
    sosRank: 20,
    byeWeek: 5,
    tier: 1,
    keyStars: ['Jalen Hurts (QB)', 'Saquon Barkley (RB)', 'A.J. Brown (WR)'],
    notes: 'Premier backfield with high-end offensive line talent.',
  },
  CIN: {
    abbr: 'CIN',
    projectedWins: 10.3,
    fpi: 5.4,
    fpiRank: 7,
    sosRank: 9,
    byeWeek: 12,
    tier: 2,
    keyStars: ['Joe Burrow (QB)', 'Ja\'Marr Chase (WR)', 'Tee Higgins (WR)'],
    notes: 'High-volume passing game capable of outscoring any opponent.',
  },
  HOU: {
    abbr: 'HOU',
    projectedWins: 10.1,
    fpi: 5.1,
    fpiRank: 8,
    sosRank: 22,
    byeWeek: 14,
    tier: 2,
    keyStars: ['C.J. Stroud (QB)', 'Nico Collins (WR)', 'Will Anderson Jr. (DE)'],
    notes: 'Rapidly emerging AFC power with dynamic quarterback play.',
  },
  GB: {
    abbr: 'GB',
    projectedWins: 10.0,
    fpi: 4.8,
    fpiRank: 9,
    sosRank: 16,
    byeWeek: 10,
    tier: 2,
    keyStars: ['Jordan Love (QB)', 'Josh Jacobs (RB)', 'Rashan Gary (LB)'],
    notes: 'Young, explosive offensive core with strong secondary depth.',
  },
  DAL: {
    abbr: 'DAL',
    projectedWins: 9.9,
    fpi: 4.5,
    fpiRank: 10,
    sosRank: 13,
    byeWeek: 7,
    tier: 2,
    keyStars: ['Dak Prescott (QB)', 'CeeDee Lamb (WR)', 'Micah Parsons (LB)'],
    notes: 'Elite regular season win consistency with pass-rush dominance.',
  },
  MIA: {
    abbr: 'MIA',
    projectedWins: 9.7,
    fpi: 4.2,
    fpiRank: 11,
    sosRank: 15,
    byeWeek: 6,
    tier: 2,
    keyStars: ['Tua Tagovailoa (QB)', 'Tyreek Hill (WR)', 'Jaylen Waddle (WR)'],
    notes: 'Blistering track speed offense; dynamic early season scoring.',
  },
  NYJ: {
    abbr: 'NYJ',
    projectedWins: 9.6,
    fpi: 3.9,
    fpiRank: 12,
    sosRank: 17,
    byeWeek: 12,
    tier: 2,
    keyStars: ['Aaron Rodgers (QB)', 'Breece Hall (RB)', 'Sauce Gardner (CB)'],
    notes: 'Top-3 defense in NFL paired with high-upside veteran quarterbacking.',
  },
  LAR: {
    abbr: 'LAR',
    projectedWins: 9.4,
    fpi: 3.5,
    fpiRank: 13,
    sosRank: 11,
    byeWeek: 6,
    tier: 2,
    keyStars: ['Matthew Stafford (QB)', 'Puka Nacua (WR)', 'Cooper Kupp (WR)'],
    notes: 'Masterful Sean McVay scheme with lethal target distribution.',
  },
  LAC: {
    abbr: 'LAC',
    projectedWins: 9.1,
    fpi: 2.8,
    fpiRank: 14,
    sosRank: 24,
    byeWeek: 5,
    tier: 3,
    keyStars: ['Justin Herbert (QB)', 'Derwin James (S)', 'Joey Bosa (DE)'],
    notes: 'Physical coaching overhaul and elite arm talent under center.',
  },
  TB: {
    abbr: 'TB',
    projectedWins: 8.9,
    fpi: 2.2,
    fpiRank: 15,
    sosRank: 25,
    byeWeek: 11,
    tier: 3,
    keyStars: ['Baker Mayfield (QB)', 'Mike Evans (WR)', 'Antoine Winfield Jr. (S)'],
    notes: 'Favorable NFC South path and resilient offensive clutch play.',
  },
  CHI: {
    abbr: 'CHI',
    projectedWins: 8.8,
    fpi: 2.0,
    fpiRank: 16,
    sosRank: 19,
    byeWeek: 7,
    tier: 3,
    keyStars: ['Caleb Williams (QB)', 'DJ Moore (WR)', 'Montez Sweat (DE)'],
    notes: 'Generational rookie QB talent with a revamped, loaded receiving corps.',
  },
  PIT: {
    abbr: 'PIT',
    projectedWins: 8.7,
    fpi: 1.8,
    fpiRank: 17,
    sosRank: 6,
    byeWeek: 9,
    tier: 3,
    keyStars: ['T.J. Watt (LB)', 'George Pickens (WR)', 'Minkah Fitzpatrick (S)'],
    notes: 'Mike Tomlin floor guarantee: never finished below .500.',
  },
  JAX: {
    abbr: 'JAX',
    projectedWins: 8.6,
    fpi: 1.5,
    fpiRank: 18,
    sosRank: 23,
    byeWeek: 12,
    tier: 3,
    keyStars: ['Trevor Lawrence (QB)', 'Travis Etienne Jr. (RB)', 'Josh Hines-Allen (DE)'],
    notes: 'High ceiling if Trevor Lawrence finds rhythm in division matchups.',
  },
  IND: {
    abbr: 'IND',
    projectedWins: 8.5,
    fpi: 1.2,
    fpiRank: 19,
    sosRank: 26,
    byeWeek: 14,
    tier: 3,
    keyStars: ['Anthony Richardson (QB)', 'Jonathan Taylor (RB)', 'DeForest Buckner (DT)'],
    notes: 'Explosive dual-threat ground game under Shane Steichen.',
  },
  SEA: {
    abbr: 'SEA',
    projectedWins: 8.4,
    fpi: 0.9,
    fpiRank: 20,
    sosRank: 15,
    byeWeek: 10,
    tier: 3,
    keyStars: ['Geno Smith (QB)', 'DK Metcalf (WR)', 'Devon Witherspoon (CB)'],
    notes: 'Aggressive defensive scheme and deep receiving weapons.',
  },
  CLE: {
    abbr: 'CLE',
    projectedWins: 8.3,
    fpi: 0.8,
    fpiRank: 21,
    sosRank: 7,
    byeWeek: 10,
    tier: 3,
    keyStars: ['Myles Garrett (DE)', 'Nick Chubb (RB)', 'Amari Cooper (WR)'],
    notes: 'Reigning DPOY Myles Garrett anchors a suffocating defense.',
  },
  ATL: {
    abbr: 'ATL',
    projectedWins: 8.2,
    fpi: 0.6,
    fpiRank: 22,
    sosRank: 30,
    byeWeek: 12,
    tier: 3,
    keyStars: ['Kirk Cousins (QB)', 'Bijan Robinson (RB)', 'Drake London (WR)'],
    notes: 'Softest schedule in the NFL with elite young skill players.',
  },
  MIN: {
    abbr: 'MIN',
    projectedWins: 8.0,
    fpi: 0.2,
    fpiRank: 23,
    sosRank: 14,
    byeWeek: 6,
    tier: 3,
    keyStars: ['Justin Jefferson (WR)', 'Brian Flores (DC)', 'Aaron Jones (RB)'],
    notes: 'Justin Jefferson is the league premier WR; exotic blitz-heavy defense.',
  },
  ARI: {
    abbr: 'ARI',
    projectedWins: 7.7,
    fpi: -0.5,
    fpiRank: 24,
    sosRank: 10,
    byeWeek: 11,
    tier: 4,
    keyStars: ['Kyler Murray (QB)', 'Marvin Harrison Jr. (WR)', 'James Conner (RB)'],
    notes: 'Dynamic offense with Kyler + Marvin Harrison Jr. sleeper synergy.',
  },
  WSH: {
    abbr: 'WSH',
    projectedWins: 7.5,
    fpi: -1.2,
    fpiRank: 25,
    sosRank: 21,
    byeWeek: 14,
    tier: 4,
    keyStars: ['Jayden Daniels (QB)', 'Terry McLaurin (WR)', 'Frankie Luvu (LB)'],
    notes: 'Heisman dual-threat QB brings immediate scoring fireworks.',
  },
  NO: {
    abbr: 'NO',
    projectedWins: 7.3,
    fpi: -1.8,
    fpiRank: 26,
    sosRank: 28,
    byeWeek: 12,
    tier: 4,
    keyStars: ['Derek Carr (QB)', 'Alvin Kamara (RB)', 'Chris Olave (WR)'],
    notes: 'Veteran defense keeping games tight in a wide-open division.',
  },
  LV: {
    abbr: 'LV',
    projectedWins: 6.9,
    fpi: -2.5,
    fpiRank: 27,
    sosRank: 9,
    byeWeek: 10,
    tier: 4,
    keyStars: ['Maxx Crosby (DE)', 'Davante Adams (WR)', 'Christian Wilkins (DT)'],
    notes: 'Fierce defensive line front led by the relentless Maxx Crosby.',
  },
  TEN: {
    abbr: 'TEN',
    projectedWins: 6.6,
    fpi: -3.2,
    fpiRank: 28,
    sosRank: 20,
    byeWeek: 5,
    tier: 4,
    keyStars: ['Will Levis (QB)', 'Calvin Ridley (WR)', 'Jeffery Simmons (DT)'],
    notes: 'Transitioning to modern spread offense; high variance outcome.',
  },
  DEN: {
    abbr: 'DEN',
    projectedWins: 6.4,
    fpi: -3.8,
    fpiRank: 29,
    sosRank: 8,
    byeWeek: 14,
    tier: 4,
    keyStars: ['Bo Nix (QB)', 'Patrick Surtain II (CB)', 'Courtland Sutton (WR)'],
    notes: 'Sean Payton disciplined structure with lockdown corner Patrick Surtain.',
  },
  NYG: {
    abbr: 'NYG',
    projectedWins: 6.1,
    fpi: -4.5,
    fpiRank: 30,
    sosRank: 5,
    byeWeek: 11,
    tier: 5,
    keyStars: ['Malik Nabers (WR)', 'Dexter Lawrence (DT)', 'Brian Burns (DE)'],
    notes: 'Elite interior pass rush, but brutal NFC East schedule.',
  },
  NE: {
    abbr: 'NE',
    projectedWins: 5.5,
    fpi: -5.8,
    fpiRank: 31,
    sosRank: 4,
    byeWeek: 14,
    tier: 5,
    keyStars: ['Drake Maye (QB)', 'Christian Gonzalez (CB)', 'Rhamondre Stevenson (RB)'],
    notes: 'Rebuilding under new coaching era with rookie QB upside.',
  },
  CAR: {
    abbr: 'CAR',
    projectedWins: 5.2,
    fpi: -6.5,
    fpiRank: 32,
    sosRank: 27,
    byeWeek: 11,
    tier: 5,
    keyStars: ['Bryce Young (QB)', 'Derrick Brown (DT)', 'Diontae Johnson (WR)'],
    notes: 'Bargain-bin pricing; Dave Canales offensive rehabilitation project.',
  },
};
